"use client";

import { LegalPageLayout } from "@/components/legal/legal-page-layout";

export default function DataDeletionPage() {
  return (
    <LegalPageLayout title="Data Deletion" lastUpdated="May 2026">
      <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Delete Specific Data Without Deleting Your Account
          </h2>
          <p>
            You can delete specific data from your GrowthSync account directly in the app without deleting your entire account. This allows you to remove particular information while keeping your account active.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Data You Can Delete Yourself
          </h2>
          <p>
            The following data can be deleted directly in the app:
          </p>
          <ul className="list-disc pl-6 space-y-3 mt-4">
            <li>
              <strong>Community posts:</strong> You can delete your own posts in any community by opening the post and selecting the delete option. This permanently removes the post and all associated comments.
            </li>
            <li>
              <strong>Profile information:</strong> You can edit your profile at any time through <strong>Settings → Profile</strong> to remove or update information such as your headline, bio, photos, and other profile details. Simply edit the fields and save to update or remove information.
            </li>
            <li>
              <strong>Messages and conversations:</strong> You can delete individual message threads by opening the conversation and using the delete option. This removes the conversation from your messages.
            </li>
            <li>
              <strong>Connections/Matches:</strong> You can unmatch or remove connections directly from your matches or connections list. This removes the connection data between you and the other user.
            </li>
            <li>
              <strong>Event RSVPs:</strong> You can cancel your RSVP for any event you've registered for, which removes your attendance data for that event.
            </li>
            <li>
              <strong>Community memberships:</strong> You can leave any community you've joined, which removes your membership data for that community.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            How to Delete Data in the App
          </h2>
          <p>
            To delete specific data, follow these steps:
          </p>
          <ol className="list-decimal pl-6 space-y-3 mt-4">
            <li>Open the GrowthSync app and log in to your account</li>
            <li>Navigate to the section containing the data you want to delete (e.g., Messages, Communities, Profile, Events)</li>
            <li>Find the specific item you want to delete (post, message, connection, etc.)</li>
            <li>Use the delete or remove option available for that item</li>
            <li>Confirm the deletion when prompted</li>
          </ol>
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 mt-6">
            <p className="text-sm text-amber-900">
              <strong>Note:</strong> Deletion is permanent and cannot be undone. Make sure you want to delete the data before confirming.
            </p>
          </div>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            What Happens After Deletion
          </h2>
          <p>
            When you delete data in the app:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>The specified data will be permanently removed from our systems</li>
            <li>Your account will remain active with all other data intact</li>
            <li>Deletion is immediate and you'll see the changes right away</li>
            <li>Some data may be retained temporarily for legal or security purposes (as outlined in our Privacy Policy)</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Export Your Data First
          </h2>
          <p>
            Before requesting deletion, you may want to export your data to keep a copy. You can do this in the app:
          </p>
          <ol className="list-decimal pl-6 space-y-2 mt-4">
            <li>Open the GrowthSync app</li>
            <li>Go to <strong>Settings</strong></li>
            <li>Scroll to <strong>Your Data Rights</strong></li>
            <li>Tap <strong>Export Data</strong> to download a copy of your data</li>
          </ol>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Full Account Deletion
          </h2>
          <p>
            If you want to delete your entire account and all associated data, please see our{" "}
            <a href="/account-deletion" className="text-blue-600 hover:text-blue-700 hover:underline">Account Deletion page</a>.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Additional Information
          </h2>
          <p>
            For more information about your data rights and how we handle your data, please review our{" "}
            <a href="/privacy" className="text-blue-600 hover:text-blue-700 hover:underline">Privacy Policy</a> and{" "}
            <a href="/terms" className="text-blue-600 hover:text-blue-700 hover:underline">Terms of Service</a>.
          </p>
          <p className="mt-4">
            <strong>Company:</strong> GrowthSync Network Ltd<br />
            <strong>Registered Office:</strong> Unit 6, Balfour Court, Leyland, PR25 2TF, United Kingdom<br />
            <strong>Email:</strong> <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
}
